"use client";

import { useEmailValidation } from "@/components/hooks/useEmailValidation";
import { useTranslation } from "@/context/LanguageContext";
import { useUser } from "@/context/UserContext";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import {
    buildCleanName,
    buildMappedUser,
    fetchCurrentUser,
    fetchTiposIdentificacion,
    handleLoginError,
    loginRequest,
} from "../Services/loginService";
import type { TipoIdentificacion } from "../../Types/types";
import { Tab } from "../Types/types";

export const useLoginPage = () => {
    const { t } = useTranslation();
    // Siempre inicia true para coincidir con SSR; useEffect decide si saltar
    const [showSplash, setShowSplash] = useState(true);

    // Si el splash ya se mostró en esta sesión, saltarlo inmediatamente
    useEffect(() => {
        try {
            if (sessionStorage.getItem("splashShown") === "true") {
                setShowSplash(false);
            }
        } catch { /* SSR-safe */ }
    }, []);

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [tipoDocumento, setTipoDocumento] = useState("");
    const [numeroDocumento, setNumeroDocumento] = useState("");
    const [numeroDocumentoError, setNumeroDocumentoError] = useState("");
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const { emailError, handleEmailChange } = useEmailValidation();
    const [rememberMe, setRememberMe] = useState(false);

    const searchParams = useSearchParams();
    const router = useRouter();

    const tabParam = searchParams.get("tab");

    const [tab, setTab] = useState<Tab>(
        tabParam === "register" ? "register" : "login"
    );

    const [loading, setLoading] = useState(false);

    const prevTab = useRef<Tab>("login");

    useEffect(() => {
        prevTab.current = tab;
    }, [tab]);

    const [tiposIdentificacion, setTiposIdentificacion] = useState<TipoIdentificacion[]>([]);
    const [loadingTipos, setLoadingTipos] = useState(true);

    const [apiErrorUser, setApiErrorUser] = useState<string | null>(null);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successData, setSuccessData] = useState<{ title: string; message: string } | null>(null);

    const { setUser, isLoadingUser, user, loadConfiguracion } = useUser();

    // Handlers correo
    const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setEmail(value);
        handleEmailChange(value);
    };

    // Handlers contraseña
    const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
    };

    // Handler tipo documento
    const handleTipoDocumentoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setTipoDocumento(e.target.value);
        if (numeroDocumentoError) setNumeroDocumentoError("");
    };

    // Handler número documento
    const handleNumeroDocumentoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNumeroDocumento(value);
        if (!/^\d*$/.test(value)) {
            setNumeroDocumentoError("El número de documento solo puede contener dígitos");
        } else {
            setNumeroDocumentoError("");
        }
    };

    // Handler checkbox
    const handleAcceptTermsChange = (checked: boolean) => {
        setAcceptTerms(checked);
    };

    const handleRememberMeChange = (checked: boolean) => {
        setRememberMe(checked);
    };

    // Handler para reCAPTCHA
    const handleRecaptchaVerify = (token: string | null) => {
        setRecaptchaToken(token);
    };

    const handleRecaptchaError = () => {
        setRecaptchaToken(null);
        setApiErrorUser("Error al verificar reCAPTCHA. Por favor intenta de nuevo.");
    };

    const handleRecaptchaExpired = () => {
        setRecaptchaToken(null);
        setApiErrorUser("reCAPTCHA expirado. Por favor verifica nuevamente.");
    };

    // Handler para cerrar el alert de error
    const handleCloseErrorAlert = () => {
        setShowErrorAlert(false);
        setApiErrorUser(null);
    };

    // Handler para cerrar el alert de éxito
    const handleCloseSuccessAlert = () => {
        setShowSuccessAlert(false);
        setSuccessData(null);
    };

    // Verificar si el captcha está activo
    const isCaptchaActive = process.env.NEXT_PUBLIC_CAPTCHA_IS_ACTIVE !== "false";

    // Verificar si los campos básicos son válidos (para habilitar el checkbox)
    const areBasicFieldsValid =
        tipoDocumento.trim() !== "" &&
        numeroDocumento.trim() !== "" &&
        !numeroDocumentoError &&
        email.trim() !== "" &&
        password.trim() !== "" &&
        !emailError;

    // Verificar si todos los campos son válidos (incluyendo términos y captcha)
    const areAllFieldsValid =
        areBasicFieldsValid &&
        acceptTerms &&
        (isCaptchaActive ? recaptchaToken !== null : true);

    const handleTabChange = (newTab: Tab) => {
        setTab(newTab);
        router.replace(`/login?tab=${newTab}`);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const isEmailValid = handleEmailChange(email);

        if (
            !tipoDocumento ||
            !numeroDocumento ||
            numeroDocumentoError ||
            !isEmailValid ||
            !acceptTerms ||
            (isCaptchaActive && !recaptchaToken)
        ) {
            const missingCaptcha = isCaptchaActive && !recaptchaToken;
            setApiErrorUser(
                missingCaptcha
                    ? "Por favor completa todos los campos y verifica el reCAPTCHA."
                    : "Por favor completa todos los campos requeridos."
            );
            return;
        }

        try {
            setLoading(true);
            setApiErrorUser(null);

            const data = await loginRequest({
                correo: email,
                clave: password,
                numero_identificacion: numeroDocumento,
                tipo_identificacion: tipoDocumento,
            });

            if (data.userId) {
                const cleanName = buildCleanName(data.name);

                setSuccessData({
                    title: t("alerts.loginSuccess"),
                    message: t("alerts.loginSuccessMessage").replace("{name}", cleanName),
                });

                setShowSuccessAlert(true);

                setUser({
                    userId: data.userId,
                    type: data.type,
                    name: cleanName,
                });

                try {
                    const meData = await fetchCurrentUser();

                    if (meData.user && meData.user.data) {
                        const mappedUser = buildMappedUser(meData.user.data);
                        setUser(mappedUser);
                        await loadConfiguracion();
                    }
                } catch (userErr) {
                    console.error("Error obteniendo datos completos del usuario:", userErr);
                }
            }
        } catch (err) {
            const friendly = handleLoginError(err);
            setApiErrorUser(friendly);
        } finally {
            setLoading(false);
        }
    };

    // Cargar tipos de identificación al montar el componente
    useEffect(() => {
        const loadTipos = async () => {
            try {
                setLoadingTipos(true);
                const tipos = await fetchTiposIdentificacion();
                setTiposIdentificacion(tipos);
            } catch (error) {
                console.error("Error cargando tipos de identificación:", error);
            } finally {
                setLoadingTipos(false);
            }
        };

        loadTipos();
    }, []);

    // Mostrar alert cuando hay error de API
    useEffect(() => {
        if (apiErrorUser) {
            setShowErrorAlert(true);
        }
    }, [apiErrorUser]);

    // Redirigir si el usuario ya está autenticado
    useEffect(() => {
        if (!isLoadingUser && user) {
            router.push("/home");
        }
    }, [isLoadingUser, user, router]);

    // Handler para cuando termine el splash screen
    const handleSplashFinish = () => {
        try {
            sessionStorage.setItem("splashShown", "true");
        } catch { /* SSR-safe */ }
        setShowSplash(false);
    };

    return {
        // estado del formulario
        email,
        password,
        emailError,
        tab,
        acceptTerms,
        isCheckboxDisabled: !areBasicFieldsValid,
        isSubmitDisabled: !areAllFieldsValid,
        tipoDocumento,
        numeroDocumento,
        numeroDocumentoError,
        loading,
        tiposIdentificacion,
        loadingTipos,
        rememberMe,
        // estado de alertas
        apiErrorUser,
        showErrorAlert,
        showSuccessAlert,
        successData,
        // splash
        showSplash,
        // handlers
        onEmailChange: handleEmailInputChange,
        onPasswordChange: handlePasswordInputChange,
        onSubmit: handleSubmit,
        onAcceptTermsChange: handleAcceptTermsChange,
        onRememberMeChange: handleRememberMeChange,
        onTipoDocumentoChange: handleTipoDocumentoChange,
        onNumeroDocumentoChange: handleNumeroDocumentoChange,
        onRecaptchaVerify: handleRecaptchaVerify,
        onRecaptchaError: handleRecaptchaError,
        onRecaptchaExpired: handleRecaptchaExpired,
        onCloseErrorAlert: handleCloseErrorAlert,
        onCloseSuccessAlert: handleCloseSuccessAlert,
        onTabChange: handleTabChange,
        onSplashFinish: handleSplashFinish,
    };
};
